// Auth Types
export type LoginError = {
    message: string
}

export interface iLoginData {
    email_or_username: string,
    password: string
}

export type RawPermission = {
    app: string,
    codename: string,
    name: string
}

export type Group = {
    id: number
    name: string
    description?: string
    type?: string
    is_active?: boolean
    permissions?: RawPermission[]
}

export class User {
    id: number
    username: string
    first_name: string
    last_name: string
    department: Group
    groups: Group[]
    phone_number: string | null
    is_staff: boolean
    is_active: boolean
    api_access: boolean
    email: string
    permissions: RawPermission[]
    user_permissions: RawPermission[]
    last_login: Date
    deleted_at: Date

    constructor(
        id: number, 
        username: string, 
        first_name: string, 
        last_name: string, 
        department: Group, 
        groups: Group[], 
        phone_number: string | null, 
        is_staff: boolean, 
        is_active: boolean, 
        email: string, 
        user_permissions: RawPermission[],
        last_login: Date,
        deleted_at: Date
    ) {
        this.id = id
        this.username = username
        this.first_name = first_name
        this.last_name = last_name
        this.department = department
        this.groups = groups
        this.phone_number = phone_number
        this.is_staff = is_staff
        this.is_active = is_active
        this.email = email
        this.user_permissions = user_permissions
        this.last_login = last_login
        this.deleted_at = deleted_at

        this.permissions = user_permissions
        groups.map((g) => g.permissions.map((p) => this.permissions.push(p)))
        this.permissions = [...new Set(this.permissions)]
    }

    checkPermission(module: string, permission: string) {
        return this.permissions.filter((p) => p.app.toLowerCase() === module.toLocaleLowerCase() && p.codename.toLocaleLowerCase() === permission.toLocaleLowerCase()).length > 0
    }

    getFirstModuleWithAccess() {
        if (this.is_staff) { return "Dashboards" }
        let nonAuthApps = this.permissions.filter((perm) => (!['Custom auth', 'Auth'].includes(perm.app)))
        nonAuthApps = nonAuthApps.length>0?nonAuthApps:this.permissions
        return nonAuthApps[nonAuthApps.length-1].app
    }

    canAccessModule(module: string) {
        return !this.is_staff ? this.permissions.filter((p) => p.app === module).length > 0: true
    }
}

export type AuthContextType = {
    user: User | null
    error: LoginError | null
    userActions: {
        [isDoingAction: string]: { value: any, change: (value: any) => void }
    }
    loginAction: (loginData: iLoginData) => void;
    logoutAction: (expiredSession?: boolean) => void;
}

export type LoadingContext = {
    isLoaded: boolean
    show: () => void
    hide: () => void
}

// Coverage Types

export type CoverageEvent = {
    incomes: {
        base: number,
        simulation: number
    }
    outcomes: number
    alert: string
}

export type CurrentData = {
    inventory: number
    sales: number
    forecast: number
}

export type EstimatedCoverage = {
    targetDate: {
        year: number
        month: number
        week: number
    }
    current: CurrentData
    estimatedUnits: number
    coverageTime: number
    events: CoverageEvent
}

export type ProductAttribute = {
    name: string,
    value: any
}

export type ProductUnit = {
    code: string,
    units: number,
    cubic_measure: number,
    weigth: number
}

export type ProductProps = {
    id: number
    erpCode: string
    erp_code?: string
    description: string
    category: string
    line: string
    subLine: string
    sub_line?: string
    attributes?: ProductAttribute[]
    units?: ProductUnit[]
    /** Campos extra listado cobertura v2 (costos FOB y bulto). */
    costoFobXUnidad?: string | null
    costoFobXBulto?: string | null
    unidadXBulto?: number | null
    cbmXBulto?: number | null
    pesoXBulto?: number | null
    coverageDeviationReason?: string
}

export type ProductCoverage = {
    productProps: ProductProps
    coverage: Array<EstimatedCoverage>
}

// Buy orders types

export type IncomingProduct = {
    [attributes: string]: any
    product_code: string
    description?: string
    bulks: number
    total_units?: number
    units_per_bulk?: number
    /**
     * Datos de compra que exige `createPurchasingOrder` del datamart.
     * Opcionales: una simulación que no se va a cargar en Profit no los usa.
     */
    unit_cost?: number | null
    warehouse_code?: string
    applies_tax?: boolean
    tax_pctg?: number | null
    discount_pctg?: number | null
    uom?: string
}

export type BuyOrder = {
    number: string
    ata: Date
    ataw?: Date
    [attributes: string]: any
    /** Código de proveedor del ERP (`vendorCode`, p. ej. "0176"). */
    vendor_code?: string
    vendor_name?: string
    incoming_product_list: IncomingProduct[]
}

// Defaults de compra (GET /api/v2/coverage_simulator/products/purchase-defaults/)

export type PurchaseVendorOption = {
    vendor_code: string
    /** El código que entiende Profit. Es el que se guarda y se envía. */
    erp_code: string
    unit_price: number | null
    uom: string
    discount_pctg: number | null
    min_order_quantity: number | null
    avg_production_days: number | null
    plant_code: string
}

export type PurchaseWarehouseOption = {
    /** Identificador interno del datamart (p. ej. `PRW-01`). Sólo para mostrar. */
    warehouse_code: string
    /**
     * El código que entiende Profit (`co_alma`, p. ej. `0001`). Es el que se
     * guarda en la línea y el que viaja en `createPurchasingOrder`: mandar el
     * interno produce "No existen almacenes con código: …".
     */
    erp_code: string
    name: string
    reference_code: string
    actual_quantity: number | null
}

export type ProductPurchaseDefaults = {
    product_code: string
    description: string
    vendor_name: string
    unit_cost: number | null
    /** `datamart_vendor_price` | `local_fob` | `none` */
    unit_cost_source: string
    uom: string
    /**
     * Código de proveedor del ERP tomado del maestro de productos. Es la
     * fuente principal: lo carga quien negocia la compra.
     */
    vendor_code: string
    /** `master` | `datamart` | `none` */
    vendor_code_source: string
    /** Almacén destino del ERP, también del maestro. */
    warehouse_code: string
    /** `master` | `datamart` | `none` */
    warehouse_code_source: string
    vendors: PurchaseVendorOption[]
    warehouses: PurchaseWarehouseOption[]
}

export type VendorCatalogEntry = {
    vendor_code: string
    /** El código que entiende Profit. Es el que se guarda y se envía. */
    erp_code: string
    name: string
    reference_code: string
}

// Envío a Profit (/api/v2/simulations/{id}/simulated-purchase-orders/{po}/profit-export/)

/**
 * Los siete estados de una orden frente a Profit. `draft` no es un registro:
 * es la ausencia de envío.
 */
export type ProfitPushState =
    | "draft"
    | "sending"
    | "pending"
    | "processed"
    | "rejected"
    | "failed"
    | "unconfirmed"

export type ProfitPushStatus = {
    id: string
    order_id: string
    external_identifier: string
    state: ProfitPushState
    /** Viene resuelto del backend; no se deriva acá. */
    editable: boolean
    integration_status: string
    staging_id: number | null
    profit_order_number: string
    /** Mensaje del datamart, sin envolver. */
    message: string
    /** Campo señalado por el datamart, p. ej. `details.warehouse_code`. */
    error_field: string
    integration_error: string
    rows_created: number | null
    attempt_count: number
    sent_at: string | null
    processed_at: string | null
    last_polled_at: string | null
}

export type PurchaseDefaultsResult = {
    byCode: Record<string, ProductPurchaseDefaults>
    /** Maestro de proveedores: respaldo cuando el material no trae config. */
    vendorCatalog: VendorCatalogEntry[]
    datamartAvailable: boolean
    datamartError: string
}

// Notification

export type Notification = {
    id: number,
    sender: string
    title: string
    description: string
    hasAttachment: boolean
    createdAt: Date
    icon: string
    downloadableFileName: string
}

// Dashboards

export type Dashboard = {
    id: number,
    name: string,
    embedMode: number,
    publishedUrl: string,
    powerBIReportUUID: string,
    powerBIDatasetUUID: string,
    isActive: boolean,
    icon: string,
    backgroundImage: string,
    refreshedAt: Date,
    createdAt: Date,
    deletedAt: Date,
}